/**
 * PostHog Analytics Service
 *
 * Utility functions for PostHog SDK operations in non-React contexts (Zustand stores, etc.).
 * PostHog initialization is handled in App.tsx via PostHogProvider.
 *
 * For React components, prefer using the useAnalytics hook from AnalyticsContext.
 *
 * IMPORTANT: PostHog is only enabled in production environment.
 */
import posthog from 'posthog-js'

// Environment configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const ENV = import.meta.env.VITE_ENV

/**
 * Check if PostHog should be enabled.
 * Only enabled in production environment with a valid API key.
 */
export const isAnalyticsEnabled = (): boolean => {
  return ENV === 'production' && !!POSTHOG_KEY
}

/**
 * Track a custom event.
 * Use this in non-React contexts (Zustand stores, utility functions).
 *
 * @param eventName - Name of the event (use AnalyticsEvents constants)
 * @param properties - Optional event properties
 */
export const track = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  if (!isAnalyticsEnabled()) return

  try {
    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    })
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error)
  }
}

/**
 * Reset user identity.
 * Call this on logout to clear the user association.
 */
export const reset = (): void => {
  if (!isAnalyticsEnabled()) return

  try {
    posthog.reset()
  } catch (error) {
    console.error('[Analytics] Failed to reset identity:', error)
  }
}

/**
 * Get a feature flag value.
 * Returns the flag value or undefined if not available.
 *
 * @param flagKey - The feature flag key
 */
export const getFeatureFlag = (
  flagKey: string
): boolean | string | undefined => {
  if (!isAnalyticsEnabled()) return undefined

  try {
    return posthog.getFeatureFlag(flagKey)
  } catch (error) {
    console.error('[Analytics] Failed to get feature flag:', error)
    return undefined
  }
}

/**
 * Check if a boolean feature flag is enabled.
 *
 * @param flagKey - The feature flag key
 * @returns true if enabled, false otherwise
 */
export const isFeatureEnabled = (flagKey: string): boolean => {
  if (!isAnalyticsEnabled()) return false

  try {
    return posthog.isFeatureEnabled(flagKey) ?? false
  } catch (error) {
    console.error('[Analytics] Failed to check feature flag:', error)
    return false
  }
}

// Export as an object for backwards compatibility with existing imports
export const analyticsService = {
  isEnabled: isAnalyticsEnabled,
  track,
  reset,
  getFeatureFlag,
  isFeatureEnabled,
}
